// MultiSelect.tsx
import {
  PlusOutlined,
  DeleteOutlined,
  CloseOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { Button, Divider, Input, Segmented, Space, Select, Flex } from 'antd';
import cn from 'classnames';
import React from 'react';

import { FilterMenuShell } from '@app/shared/components/FilterMenuShell';
import { useMultiSelect } from '@app/shared/components/MultiSelect/hooks/useMultiSelect';
import type {
  FixedOption,
  GroupInnerLogic,
  GroupsRelation,
  MultiSelectOption,
  MultiSelectProps,
} from '@app/shared/components/MultiSelect/types';
import { MultiSelectVirtualList } from '@app/shared/components/MultiSelect/ui/MultiSelectVirtualList';
import { generateSpanWithDataTestId } from '@app/shared/utils';

import '@app/shared/components/MultiSelect/styles/MultiSelect.desktop.scss';

const PF = 'multi-select';
const FIXED_VALUE = '__fixed__';

const getOrderedOptionsForGroup = (
  filteredOptions: MultiSelectOption[],
  selectedValues: string[]
): MultiSelectOption[] => {
  const selectedSet = new Set<string>(selectedValues);

  return [...filteredOptions].sort((a, b) => {
    const aChecked = selectedSet.has(a.value);
    const bChecked = selectedSet.has(b.value);

    if (aChecked !== bChecked) return aChecked ? -1 : 1;

    const isADeleted = Boolean(a.isDeleted);
    const isBDeleted = Boolean(b.isDeleted);

    if (isADeleted !== isBDeleted) return isADeleted ? 1 : -1;

    return (a.label ?? '').localeCompare(b.label ?? '');
  });
};

export const MultiSelect = ({
  className = '',
  placeholder = '',
  placeHolderDataTestId = '',
  dataTestId = '',
  loading = false,
  disabled = false,
  allowClear = false,
  size = 'middle',
  options,
  value,
  onChange,
  fixedInputLabel,
  dropdownDataTestId = '',
  optionDataTestId = '',
  activeCheckboxDataTestId = '',
  deactivateCheckboxDataTestId = '',
  inputDataTestId = '',
  classNames,
  ...rest
}: MultiSelectProps) => {
  const {
    groups,
    allSelectedValues,
    searchByGroup,
    setGroupSearch,
    getFilteredOptions,
    renderOptionLabel,
    setGroupLogic,
    setBetweenRelation,
    toggleValueInGroup,
    selectAllFilteredInGroup,
    resetGroupSelection,
    addGroup,
    removeGroup,
    clearAll,
    handleDropdownVisibleChange,
  } = useMultiSelect({ options, value, onChange });

  const menuTitle =
    typeof fixedInputLabel === 'string' ? fixedInputLabel : 'Теги клиентов';

  const fixedLabel = (
    <span className={`${PF}__fixed-label`}>
      {fixedInputLabel ?? 'Теги клиентов'}
      {!!allSelectedValues.length && (
        <span className={`${PF}__fixed-count`}>{allSelectedValues.length}</span>
      )}
    </span>
  );

  const fixedOptions: FixedOption[] = [
    { value: FIXED_VALUE, label: fixedLabel },
  ];

  return (
    <Select<string, FixedOption>
      {...rest}
      value={FIXED_VALUE}
      options={fixedOptions}
      disabled={disabled}
      loading={loading}
      size={size}
      showSearch={false}
      allowClear={allowClear && allSelectedValues.length > 0}
      onClear={clearAll}
      className={`${PF} ${className}`}
      popupMatchSelectWidth={false}
      virtual
      placeholder={generateSpanWithDataTestId(
        placeholder,
        placeHolderDataTestId
      )}
      {...(dataTestId && { 'data-testid': dataTestId })}
      onOpenChange={handleDropdownVisibleChange}
      classNames={{
        ...classNames,
        popup: {
          ...classNames?.popup,
          root: cn(`${PF}__popup-wrapper`, classNames?.popup?.root),
        },
      }}
      optionRender={(option) => (
        <span data-testid={optionDataTestId}>{option.label}</span>
      )}
      popupRender={() => (
        <FilterMenuShell
          className={`${PF}__popup`}
          title={menuTitle}
          footer={
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addGroup}
              data-testid="add-list-button"
            >
              Добавить список
            </Button>
          }
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div
            className={`${PF}__groups`}
            {...(dropdownDataTestId && {
              'data-testid': dropdownDataTestId,
            })}
          >
            {groups.map((g, idx) => (
              <React.Fragment key={g.id}>
                <div className={`${PF}__group`}>
                  <div className={`${PF}__group__header`}>
                    <Flex
                      justify="space-between"
                      className="full-width"
                      gap={8}
                    >
                      <Input
                        allowClear
                        placeholder="Поиск по названию"
                        value={searchByGroup[g.id] ?? ''}
                        onChange={(e) => setGroupSearch(g.id, e.target.value)}
                        data-testid={inputDataTestId}
                      />
                      <Segmented
                        className={`${PF}__group__logic`}
                        options={[
                          {
                            label: generateSpanWithDataTestId(
                              'И',
                              'and-filter-button'
                            ),
                            value: 'AND',
                          },
                          {
                            label: generateSpanWithDataTestId(
                              'ИЛИ',
                              'or-filter-button'
                            ),
                            value: 'OR',
                          },
                          {
                            label: generateSpanWithDataTestId(
                              'НЕТ',
                              'not-filter-button'
                            ),
                            value: 'NOT',
                          },
                        ]}
                        value={g.logic}
                        onChange={(val) =>
                          setGroupLogic(g.id, val as GroupInnerLogic)
                        }
                      />
                      {groups.length > 1 && (
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeGroup(g.id)}
                          className={`${PF}__group__delete `}
                        />
                      )}
                    </Flex>
                  </div>

                  <div className={`${PF}__group__tools`}>
                    <Space size={8} className={`${PF}__group__actions`}>
                      {g.selectedValues.length !== options.length && (
                        <Button
                          size="small"
                          onClick={() => selectAllFilteredInGroup(g.id)}
                          icon={<CheckOutlined />}
                          data-testid="select-all"
                        >
                          Выбрать все
                        </Button>
                      )}
                      {g.selectedValues.length > 0 && (
                        <Button
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() => resetGroupSelection(g.id)}
                          data-testid="clear-all"
                        >
                          Очистить все
                        </Button>
                      )}
                    </Space>
                  </div>

                  <MultiSelectVirtualList
                    key={`${g.id}:${searchByGroup[g.id] ?? ''}`}
                    options={getOrderedOptionsForGroup(
                      getFilteredOptions(g.id),
                      g.selectedValues
                    )}
                    selectedValueSet={new Set<string>(g.selectedValues)}
                    optionDataTestId={optionDataTestId}
                    activeCheckboxDataTestId={activeCheckboxDataTestId}
                    deactivateCheckboxDataTestId={deactivateCheckboxDataTestId}
                    onToggle={(optionValue, checked) =>
                      toggleValueInGroup(g.id, optionValue, checked)
                    }
                    renderOptionLabel={renderOptionLabel}
                  />

                  {idx < groups.length - 1 && (
                    <Divider className={`${PF}__divider`} />
                  )}
                </div>

                {/* Связка хранится в relationToPrev следующего списка — так её
                    читают мапперы фильтров (OuterInclude). */}
                {groups.length > 1 && idx < groups.length - 1 && (
                  <div className={`${PF}__between`}>
                    <Segmented
                      className={`${PF}__between__segmented`}
                      options={[
                        { label: 'И', value: 'AND' },
                        { label: 'ИЛИ', value: 'OR' },
                      ]}
                      value={groups[idx + 1].relationToPrev ?? 'AND'}
                      onChange={(val) =>
                        setBetweenRelation(
                          groups[idx + 1].id,
                          val as GroupsRelation
                        )
                      }
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </FilterMenuShell>
      )}
    />
  );
};
